import { memo, useCallback, useEffect, useRef, useState } from "react";
import type { TextInput } from "react-native";
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetScrollView } from "@gorhom/bottom-sheet";
import type { BottomSheetBackdropProps } from "@gorhom/bottom-sheet";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View, Text, Button, Card, Banner } from "@src/components";
import { Switch } from "@src/components/Switch";
import { phoneFormatter } from "@src/components/FormInput";
import { BottomSheetFormInput } from "@src/components/FormInput/BottomSheetFormInput";
import type { SiteWideContact } from "@src/features/alarms/types/site-wide-contact";

export type ManageContactResult = {
  emailEnabled: boolean;
  smsEnabled: boolean;
  phoneNumber?: string;
};

interface ManageContactBottomSheetProps {
  bottomSheetRef: React.RefObject<BottomSheetModal | null>;
  contact: SiteWideContact | null;
  onSave: (contact: SiteWideContact, result: ManageContactResult) => void;
  isSaving?: boolean;
}

export const ManageContactBottomSheet = memo(
  ({ bottomSheetRef, contact, onSave, isSaving }: ManageContactBottomSheetProps) => {
    const { theme } = useUnistyles();
    const { bottom } = useSafeAreaInsets();

    const [emailEnabled, setEmailEnabled] = useState(false);
    const [smsEnabled, setSmsEnabled] = useState(false);
    const phoneNumberRef = useRef("");
    const phoneInputRef = useRef<TextInput>(null);
    const [error, setError] = useState<string>();

    // Sync state when contact changes
    useEffect(() => {
      if (contact) {
        setEmailEnabled(contact.methods.includes("Email"));
        setSmsEnabled(contact.methods.includes("SMS"));
        phoneNumberRef.current = contact.phoneNumber ?? "";
        phoneInputRef.current?.setNativeProps({ text: contact.phoneNumber ?? "" });
        setError(undefined);
      }
    }, [contact]);

    const willBeRemoved = !emailEnabled && !smsEnabled;

    const handleSave = useCallback(() => {
      if (!contact) return;
      const phoneNumber = phoneNumberRef.current;

      if (smsEnabled && !phoneNumber.trim()) {
        setError("Mobile number is required");
        return;
      }
      if (smsEnabled && !/^\+\d{6,15}$/.test(phoneNumber.trim())) {
        setError("Use international format, e.g. +61123456789");
        return;
      }

      onSave(contact, {
        emailEnabled,
        smsEnabled,
        phoneNumber: smsEnabled ? phoneNumber.trim() : undefined,
      });
    }, [contact, emailEnabled, smsEnabled, onSave]);

    const handlePhoneChange = useCallback((value: string) => {
      phoneNumberRef.current = value;
      if (value.trim()) setError(undefined);
    }, []);

    const renderBackdrop = useCallback(
      (props: BottomSheetBackdropProps) => <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} />,
      [],
    );

    return (
      <BottomSheetModal
        ref={bottomSheetRef}
        enableDynamicSizing
        enablePanDownToClose
        keyboardBehavior="interactive"
        keyboardBlurBehavior="restore"
        android_keyboardInputMode="adjustResize"
        backgroundStyle={{ backgroundColor: theme.colors.surfaceCard }}
        backdropComponent={renderBackdrop}
        handleIndicatorStyle={{ backgroundColor: theme.colors.field.border_default, width: 46 }}
        handleStyle={{ paddingTop: theme.spacing["6xl"] }}
      >
        <BottomSheetScrollView
          contentContainerStyle={{
            paddingBottom: bottom + theme.spacing["5xl"],
            paddingLeft: theme.spacing["5xl"],
            paddingRight: theme.spacing["5xl"],
          }}
          keyboardDismissMode="on-drag"
          keyboardShouldPersistTaps="handled"
        >
          <Text textStyle="md/medium" mb="md">
            Manage notifications
          </Text>
          <Text textStyle="xs/regular" color="textMuted" mb="4xl">
            Choose how this contact receives alarm notifications for the cameras on this site.
          </Text>

          <Card style={styles.card}>
            {/* Email row */}
            <View style={styles.methodRow}>
              <View flex={1}>
                <Text textStyle="sm/medium">Email</Text>
                <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
                  {contact?.email ?? "No email added"}
                </Text>
              </View>
              <Switch
                active={emailEnabled}
                onToggle={() => setEmailEnabled((prev) => !prev)}
                size="large"
                disabled={!contact?.email}
              />
            </View>

            <View style={styles.divider} />

            {/* SMS row */}
            <View style={styles.methodRow}>
              <View flex={1}>
                <Text textStyle="sm/medium">SMS</Text>
                <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
                  {contact?.phoneNumber ?? "No mobile number added"}
                </Text>
              </View>
              <Switch active={smsEnabled} onToggle={() => setSmsEnabled((prev) => !prev)} size="large" />
            </View>

            {/* Phone input when SMS toggled on without existing number */}
            {smsEnabled && !contact?.phoneNumber && (
              <View style={styles.phoneInput}>
                <BottomSheetFormInput
                  ref={phoneInputRef}
                  placeholder="Mobile number"
                  onChangeText={handlePhoneChange}
                  size="medium"
                  formatter={phoneFormatter}
                  error={error}
                />
              </View>
            )}
          </Card>

          {willBeRemoved && (
            <View mt="3xl">
              <Banner variant="warning" title="Contact will be removed." />
            </View>
          )}

          <View mt="3xl">
            <Button variant="primary" label="Save" size="md" onPress={handleSave} loading={isSaving} />
          </View>
        </BottomSheetScrollView>
      </BottomSheetModal>
    );
  },
);

ManageContactBottomSheet.displayName = "ManageContactBottomSheet";

const styles = StyleSheet.create((theme) => ({
  container: {
    paddingHorizontal: theme.spacing["5xl"],
  },
  card: {
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
  methodRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: theme.spacing["3xl"],
    paddingHorizontal: theme.spacing["4xl"],
  },
  divider: {
    height: 1,
    backgroundColor: theme.colors.borderCard,
  },
  phoneInput: {
    paddingHorizontal: theme.spacing["4xl"],
    paddingBottom: theme.spacing["3xl"],
  },
}));
